// @file Vuex store Module for Privacy Settings
import { ACCESS, Contributor, fetchWallPrivacy, WallPrivacy } from '@padlet/wall-privacy'
import window from 'bits/global'
import { __ } from 'bits/intl'
import { PERMISSION } from 'bits/privacy_helper'
import debounce from 'lodash.debounce'
import { UserId } from 'types'
import { Module } from 'vuex'
import { RootState } from 'vuexstore/surface/types'

let debouncedSearchContributor: Function | null = null

// Keep this in sync with backend
export function sanitizeSearchContributorsQuery(query): string {
  return query.trim().replace(/[/\\()[\]{}*"'~:#&|!^?]/g, '')
}

interface PrivacySettingsState {
  contributorsSearchResults: Contributor[]
  errorMessage: string | null
  errorMessageTimeout: number | null
  isPasswordSet: boolean
  isPrivacyFetched: boolean
  isSaving: boolean
  isSearchingContributors: boolean
  contributorSearchQuery: string
  wallPrivacy: WallPrivacy
  _wallPrivacyObject: WallPrivacy
}

const blankState = (): PrivacySettingsState => ({
  contributorsSearchResults: [],
  errorMessage: null,
  errorMessageTimeout: null,
  isPasswordSet: false,
  isPrivacyFetched: false,
  isSaving: false,
  isSearchingContributors: false,
  contributorSearchQuery: '',
  wallPrivacy: null,
  _wallPrivacyObject: null,
})

const SessionsModule: Module<PrivacySettingsState, RootState> = {
  namespaced: true,
  state: blankState,
  getters: {
    isDirty(state): boolean {
      return !!state.wallPrivacy && state.wallPrivacy.hasChanges
    },
    isContributorInvited(state): (contributor) => boolean {
      return (contributor): boolean => state._wallPrivacyObject.isContributorInvited(contributor)
    },
    access(state): string {
      const rawAccess = state.wallPrivacy && state.wallPrivacy.access
      if (rawAccess === ACCESS.SECRET && state.isPasswordSet) {
        return ACCESS.PASSWORD_PROTECTED
      }
      return rawAccess
    },
    visitorsCanModerate(state): boolean {
      return !!state.wallPrivacy && [PERMISSION.EDITABLE, PERMISSION.ADMINABLE].includes(state.wallPrivacy.permission)
    },
    contributorSearchQuery(state): string {
      return state.contributorSearchQuery
    },
    sanitizedContributorSearchQuery(state): string {
      return sanitizeSearchContributorsQuery(state.contributorSearchQuery)
    },
    isPrivacyFetched: (state): boolean => state.isPrivacyFetched,
    isWallPrivacyObjectReady: (state, getters): boolean => getters.isPrivacyFetched && !!state.wallPrivacy,
  },
  mutations: {
    FETCHED_WALL_PRIVACY_SUCCESS(state, wallPrivacy): void {
      state._wallPrivacyObject = wallPrivacy
      state.wallPrivacy = state._wallPrivacyObject.attributes
      state.isPrivacyFetched = true
      state.errorMessage = null
      state.isPasswordSet = !!wallPrivacy.password
    },
    DISCARD_SHARE_PANEL_CHANGES(state): void {
      if (!state._wallPrivacyObject || !state._wallPrivacyObject.hasChanges) return
      state._wallPrivacyObject.discardChanges()
      state.wallPrivacy = state._wallPrivacyObject.attributes
      state.isPasswordSet = !!state.wallPrivacy.password
      state.isPrivacyFetched = true
      state.isSearchingContributors = false
      state.contributorsSearchResults = []
    },
    RESET_WALL_PRIVACY(state): void {
      Object.assign(state, blankState())
      debouncedSearchContributor = null
    },
    FETCHED_WALL_PRIVACY_ERROR(state): void {
      state.isPrivacyFetched = true
    },
    SET_WALL_PRIVACY_ACCESS(state, access): void {
      state.isPasswordSet = false
      switch (access) {
        case ACCESS.PUBLIC:
          state._wallPrivacyObject.isPublic = true
          break
        case ACCESS.SECRET:
          state._wallPrivacyObject.isSecret = true
          break
        case ACCESS.PRIVATE:
          state._wallPrivacyObject.isPrivate = true
          break
        case ACCESS.MEMBERS_ONLY:
        case ACCESS.ORGWIDE:
          state._wallPrivacyObject.isSecret = true
          state._wallPrivacyObject.isTenantwide = true
          break
        case ACCESS.PASSWORD_PROTECTED:
          state._wallPrivacyObject.isSecret = true
          state.isPasswordSet = true
          if (state._wallPrivacyObject.originalAttributes.password) {
            state._wallPrivacyObject.password = state._wallPrivacyObject.originalAttributes.password
          } else {
            state._wallPrivacyObject.password = ''
          }
          break
        default:
          break
      }
      state.wallPrivacy = state._wallPrivacyObject.attributes
    },
    SET_WALL_PRIVACY_PASSWORD(state, password): void {
      state.isPasswordSet = true
      state._wallPrivacyObject.password = password
      state.wallPrivacy = state._wallPrivacyObject.attributes
    },
    SET_WALL_PRIVACY_PERMISSION(state, permission): void {
      state._wallPrivacyObject.permission = permission
      state.wallPrivacy = state._wallPrivacyObject.attributes
    },
    ADD_CONTRIBUTOR_TO_WALL_PRIVACY(state, { emailOrUserIdOrContributor, permission, extra }): void {
      try {
        state._wallPrivacyObject.addContributor(emailOrUserIdOrContributor, permission, { ...extra, synced: false })
      } catch (error) {
        console.warn(error)
      }
      state.wallPrivacy = state._wallPrivacyObject.attributes
    },
    REMOVE_CONTRIBUTOR_FROM_WALL_PRIVACY(state, emailOrUserIdOrContributor): void {
      state._wallPrivacyObject.removeContributor(emailOrUserIdOrContributor)
      state.wallPrivacy = state._wallPrivacyObject.attributes
    },
    REMOVE_CONTRIBUTOR_FROM_WALL_PRIVACY_REMOTE(state, userId): void {
      state._wallPrivacyObject.removeContributorRemote(userId)
      state.wallPrivacy = state._wallPrivacyObject.attributes
    },
    SET_PERMISSION_FOR_WALL_PRIVACY_CONTRIBUTOR(state, { permission, contributor }): void {
      state._wallPrivacyObject.setContributorPermission(contributor, permission)
      state.wallPrivacy = state._wallPrivacyObject.attributes
    },
    SAVE_WALL_PRIVACY_SETTINGS(state): void {
      state.isSaving = true
    },
    SAVE_WALL_PRIVACY_SETTINGS_SUCCESS(state, wallPrivacy): void {
      if (!state.isPrivacyFetched) return
      state.isSaving = false
      state._wallPrivacyObject = wallPrivacy
      state.wallPrivacy = state._wallPrivacyObject.attributes
    },
    SAVE_WALL_PRIVACY_SETTINGS_FAILURE(state): void {
      state.isSaving = false
    },
    SET_ERROR_MESSAGE(state, errorMessage): void {
      state.errorMessage = errorMessage
    },
    CLEAR_ERROR_MESSAGE(state): void {
      state.errorMessage = null
    },
    SEARCH_CONTRIBUTORS(state): void {
      state.isSearchingContributors = true
    },
    SEARCH_CONTRIBUTORS_SUCCESS(state, searchResults): void {
      if (!state.isPrivacyFetched) return
      state.contributorsSearchResults = searchResults.filter((r: Contributor) => !(r.userId && !r.username))
      state.isSearchingContributors = false
    },
    SEARCH_CONTRIBUTORS_ERROR(state): void {
      if (!state.isPrivacyFetched) return
      state.contributorsSearchResults = []
      state.isSearchingContributors = false
    },
    STOP_CONTRIBUTOR_SEARCH(state): void {
      state.isSearchingContributors = false
      state.contributorsSearchResults = []
      state.contributorSearchQuery = ''
    },
    RESEND_INVITATION(state, contributor): void {
      state._wallPrivacyObject.resendInvitation(contributor)
    },
    UPDATE_CONTRIBUTOR_SERACH_QUERY(state, query): void {
      state.contributorSearchQuery = query
    },
  },
  actions: {
    async fetchWallPrivacy({ rootState, commit }): Promise<void> {
      try {
        const wallPrivacy = await fetchWallPrivacy({
          privacyPolicyId: rootState.wall.privacy_policy.id,
          host: window.location.hostname,
          protocol: window.location.protocol.replace(':', ''),
          csrfToken: rootState.csrfToken,
        })
        commit('FETCHED_WALL_PRIVACY_SUCCESS', wallPrivacy)
      } catch (error) {
        commit('FETCHED_WALL_PRIVACY_ERROR')
        commit('SET_ERROR_MESSAGE', __('Sorry, we were unable to get your privacy settings.') + ' ' + error.message)
      }
    },
    setAccess({ commit }, { access }): void {
      commit('SET_WALL_PRIVACY_ACCESS', access)
    },
    setPassword({ commit }, { password }): void {
      commit('SET_WALL_PRIVACY_PASSWORD', password)
    },
    setWallPermission({ commit }, { permission }): void {
      commit('SET_WALL_PRIVACY_PERMISSION', permission)
    },
    async saveSettings({ state, commit, dispatch }): Promise<void> {
      if (!state.wallPrivacy.password && state.isPasswordSet) {
        dispatch('setErrorMessage', { errorMessage: __('You have to set a password!') })
        return
      }
      commit('SAVE_WALL_PRIVACY_SETTINGS')
      try {
        const wallPrivacy = await state._wallPrivacyObject.commit()
        commit('SAVE_WALL_PRIVACY_SETTINGS_SUCCESS', wallPrivacy)
      } catch (error) {
        const message = __('Sorry! Something went wrong. Please try saving again.')
        const errorText = error.message || error.error || ''
        const errorMessage = `${message} ${errorText}`
        commit('SAVE_WALL_PRIVACY_SETTINGS_FAILURE')
        commit('SET_ERROR_MESSAGE', errorMessage)
      }
    },
    setErrorMessage({ state, commit }, { errorMessage }): void {
      commit('SET_ERROR_MESSAGE', errorMessage)
      state.errorMessageTimeout && window.clearTimeout(state.errorMessageTimeout)
      state.errorMessageTimeout = window.setTimeout(() => {
        commit('CLEAR_ERROR_MESSAGE')
      }, 5000)
    },
    clearErrorMessage({ commit }): void {
      commit('CLEAR_ERROR_MESSAGE')
    },
    addContributorWithDefaultPermission({ state, commit }, emailOrUserIdOrContributor): void {
      const permission = state.wallPrivacy?.permission || PERMISSION.READABLE
      commit('ADD_CONTRIBUTOR_TO_WALL_PRIVACY', { emailOrUserIdOrContributor, permission })
      commit('STOP_CONTRIBUTOR_SEARCH')
    },
    removeContributor({ commit }, emailOrUserIdOrContributor): void {
      commit('REMOVE_CONTRIBUTOR_FROM_WALL_PRIVACY', emailOrUserIdOrContributor)
    },
    setPermissionForContributor({ commit }, { permission, contributor }): void {
      commit('SET_PERMISSION_FOR_WALL_PRIVACY_CONTRIBUTOR', { permission, contributor })
    },
    async searchContributors({ state, dispatch, commit }, { query }): Promise<void> {
      commit('SEARCH_CONTRIBUTORS', query)
      try {
        const searchResults: Contributor[] = await state._wallPrivacyObject.searchContributors(query)
        commit('SEARCH_CONTRIBUTORS_SUCCESS', searchResults)
      } catch (error) {
        dispatch('setErrorMessage', { errorMessage: error.message })
        commit('SEARCH_CONTRIBUTORS_ERROR', error)
      }
    },
    debouncedSearchContributors({ getters, commit, dispatch }, { query }): void {
      commit('UPDATE_CONTRIBUTOR_SERACH_QUERY', query)
      const sanitizedQuery = getters.sanitizedContributorSearchQuery
      if (sanitizedQuery.length <= 0) {
        commit('STOP_CONTRIBUTOR_SEARCH')
      } else {
        if (!debouncedSearchContributor) {
          const dispatchSearch = ({ query }): Promise<void> => dispatch('searchContributors', { query })
          debouncedSearchContributor = debounce(dispatchSearch, 250)
        }
        debouncedSearchContributor({ query: sanitizedQuery })
      }
    },
    resetWallPrivacy({ commit }): void {
      commit('RESET_WALL_PRIVACY')
    },
    discardWallPrivacyChanges({ commit }): void {
      commit('DISCARD_SHARE_PANEL_CHANGES')
    },
    resendInvitation({ commit }, contributor): void {
      commit('RESEND_INVITATION', contributor)
    },
    /**
     * Realtime updates
     */
    removeContributorRemote({ commit, getters, rootGetters, dispatch }, { user_id }: { user_id: UserId }): void {
      if (getters.isPrivacyFetched && rootGetters.canIAdminister) {
        commit('REMOVE_CONTRIBUTOR_FROM_WALL_PRIVACY_REMOTE', user_id)
      }
      if (rootGetters.isApp) {
        // put the user id inside postSurfaceState's transientPayload so it's only sent once
        // Sending the same id multiple time may break things if the user if re-added after they left the padlet
        dispatch('nativeApp/postSurfaceState', { collaborator_just_left_user_id: user_id }, { root: true })
      }
    },
  },
}

export default SessionsModule
